/**
 * Token storage that lives in memory and nowhere else.
 *
 * docs/ARCHITECTURE.md states this as a constraint, not a
 * preference: never localStorage, never sessionStorage, never a cookie. The
 * SPA is a public artefact served from a CDN, and a token in web storage
 * outlives the tab, survives a machine being walked away from, and is readable
 * by any script that ends up on the page.
 *
 * oidc-client-ts defaults to sessionStorage, so honouring the rule means
 * overriding a default rather than accepting one. This class implements the
 * same `Storage` shape backed by a plain Map.
 *
 * THE COST, planned for rather than discovered: a full page reload discards
 * everything here, so the user is signed out on every reload and in every new
 * tab. `signinSilent` with prompt=none makes that cheap when Cognito's own
 * session cookie is still valid — see AuthProvider.
 */
export class InMemoryStorage implements Storage {
  private readonly map = new Map<string, string>();

  get length(): number {
    return this.map.size;
  }

  clear(): void {
    this.map.clear();
  }

  getItem(key: string): string | null {
    return this.map.get(key) ?? null;
  }

  key(index: number): string | null {
    return Array.from(this.map.keys())[index] ?? null;
  }

  removeItem(key: string): void {
    this.map.delete(key);
  }

  setItem(key: string, value: string): void {
    this.map.set(key, value);
  }
}

/**
 * One instance for the app's lifetime. Module scope, not React state: it must
 * survive re-renders, and it must NOT survive a page load.
 */
export const memoryStore = new InMemoryStorage();
